import { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box,
  Grid,
  Stack,
  Paper,
  Avatar,
  Chip,
  Button,
  IconButton,
  Divider,
  Typography,
  Tooltip,
  TextField,
  MenuItem,
  InputAdornment,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Container,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
  alpha,
} from "@mui/material";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { useAuth } from "../../../AuthContext";
import { tokens } from "../../../theme";

import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import FamilyRestroomOutlinedIcon from "@mui/icons-material/FamilyRestroomOutlined";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import CloseIcon from "@mui/icons-material/Close";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";

const IMAGE_BASE = "https://mindsinaction.com.na/assets/media/pictures/";

/* ─── Schema ───────────────────────────────────────────────────────────── */

// Columns of students_V2 the form owns. The payload is built from this list so
// stray form keys are never posted as columns.
const STUDENT_COLUMNS = [
  "organisation_type",
  "organisation",
  "registration_type",
  "next_of_keen_id",
  "parent_id",
  "parent_relationship",
  "firstName",
  "lastName",
  "gender",
  "category",
  "cell",
  "email",
  "password",
  "studentToken",
  "age",
  "school",
  "grade",
  "physicalAddress",
  "status",
  "date",
  "image",
];

// Omitted from the payload when blank so the column is stored as NULL rather than
// "" (parent_id / next_of_keen_id are nullable foreign keys).
const NULLABLE_COLUMNS = [
  "organisation",
  "next_of_keen_id",
  "parent_id",
  "parent_relationship",
  "gender",
  "category",
  "image",
];

// Roles allowed to add, edit and delete students. Spelling follows the route guard
// in App.js ("supperAdmin" and "manager" are the values the API actually returns).
const STUDENT_MANAGER_ROLES = [
  "hr",
  "admin",
  "supperAdmin",
  "manager",
  "ceo",
  "coo",
];

const ORGANISATION_TYPES = [
  { value: "mia", label: "Minds in Action" },
  { value: "others", label: "Others" },
];

const REGISTRATION_TYPES = [
  { value: "parent", label: "On behalf of my child" },
  { value: "self", label: "Self registration" },
  { value: "Online", label: "Online" },
];

const GENDERS = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "other", label: "Other" },
];

const LINK_MODES = [
  { value: "none", label: "Not linked" },
  { value: "existing", label: "Use an existing record" },
  { value: "new", label: "Capture a new record" },
];

/* ─── Helpers ──────────────────────────────────────────────────────────── */

// Sentinel values for the dropdown filters — real ids are never these.
const ALL = "__all__";
const UNLINKED = "__unlinked__";

const today = () => new Date().toISOString().split("T")[0];

const generateToken = () =>
  Math.random().toString(36).substring(2, 15) +
  Math.random().toString(36).substring(2, 15);

const isActiveStudent = (s) => String(s?.status) === "1";

const imageUrl = (img) => (img ? `${IMAGE_BASE}${img}` : "");

const fmtDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// addData / updateData respond with the whole table, not just the row that was
// inserted, so a new record is located by the email that was just submitted.
// Searched from the end because the newest row is normally appended last.
const findRowByEmail = (rows, email) => {
  if (!Array.isArray(rows)) return null;
  const target = String(email || "").trim().toLowerCase();
  if (!target) return null;

  for (let i = rows.length - 1; i >= 0; i -= 1) {
    if (String(rows[i]?.email || "").trim().toLowerCase() === target) {
      return rows[i];
    }
  }
  return null;
};

const genderLabel = (value) =>
  GENDERS.find((g) => g.value === String(value).toLowerCase())?.label || "";

const organisationText = (s) =>
  s?.organisation_type === "others"
    ? s?.organisation || "—"
    : "Minds in Action";

const EMPTY_FORM = {
  organisation_type: "mia",
  organisation: "",
  registration_type: "",
  next_of_keen_id: "",
  parent_id: "",
  parent_relationship: "",
  firstName: "",
  lastName: "",
  gender: "",
  category: "",
  cell: "",
  email: "",
  password: "",
  studentToken: "",
  age: "",
  school: "",
  grade: "",
  physicalAddress: "",
  status: "1",
  date: today(),
  image: "",
};

// parent_V2(id, fullName, email, password, parentToken, cell, status, date)
const EMPTY_PARENT = { fullName: "", email: "", password: "", cell: "" };

// next_of_keen_V2(id, fullName, email, cell)
const EMPTY_NEXT_OF_KEEN = { fullName: "", email: "", cell: "" };

/* ─────────────────────────────────────────────────────────────────────── */

/* ─── Presentational components ────────────────────────────────────────── */
// Defined at module scope on purpose. A component declared inside Students would
// be a brand-new type on every render, so React would unmount and remount these
// subtrees on each keystroke and the focused input would lose focus after one
// character.

const FormSection = ({ ui, title, hint, children }) => (
  <Box sx={{ mb: 3 }}>
    <Typography
      variant="h6"
      sx={{
        mb: hint ? 0.35 : 1.5,
        letterSpacing: 0.8,
        textTransform: "uppercase",
        fontWeight: 700,
        color: ui.accent,
      }}
    >
      {title}
    </Typography>
    {hint && (
      <Typography variant="h6" sx={{ mb: 1.5, color: ui.textSecondary }}>
        {hint}
      </Typography>
    )}
    <Grid container spacing={2}>
      {children}
    </Grid>
  </Box>
);

const DetailRow = ({ ui, label, value, color }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography
      variant="h6"
      sx={{
        letterSpacing: 0.6,
        textTransform: "uppercase",
        color: ui.textSecondary,
        mb: 0.25,
      }}
    >
      {label}
    </Typography>
    <Typography
      variant="h5"
      sx={{
        fontWeight: 600,
        color: color || ui.textPrimary,
        wordBreak: "break-word",
      }}
    >
      {value || "—"}
    </Typography>
  </Box>
);

/* ─────────────────────────────────────────────────────────────────────── */

const Students = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";
  const { getData, addData, updateData, deleteData, userRole } = useAuth();

  const [students, setStudents] = useState([]);
  const [parents, setParents] = useState([]);
  const [nextOfKeen, setNextOfKeen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [sortBy, setSortBy] = useState("newest");

  const [orgFilter, setOrgFilter] = useState(ALL);
  const [genderFilter, setGenderFilter] = useState(ALL);
  const [schoolFilter, setSchoolFilter] = useState(ALL);
  const [parentFilter, setParentFilter] = useState(ALL);
  const [nextOfKeenFilter, setNextOfKeenFilter] = useState(ALL);

  const [openDialog, setOpenDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentToDelete, setStudentToDelete] = useState(null);

  const [formData, setFormData] = useState(EMPTY_FORM);

  // "none" | "existing" | "new" — a parent / next of keen is optional, but when one
  // is wanted it is created before the student so its id can be referenced.
  const [parentMode, setParentMode] = useState("none");
  const [nextOfKeenMode, setNextOfKeenMode] = useState("none");
  const [parentForm, setParentForm] = useState(EMPTY_PARENT);
  const [nextOfKeenForm, setNextOfKeenForm] = useState(EMPTY_NEXT_OF_KEEN);

  const canManageStudents = STUDENT_MANAGER_ROLES.includes(userRole);

  /* ---------------- Derived design tokens ---------------- */
  const ui = useMemo(() => {
    const accent = colors.teal[500];
    const accent2 = colors.newTeal[500];
    return {
      accent,
      accent2,
      pageBg: isDark ? colors.dark[800] : "#e9ebef",
      surface: isDark ? colors.dark[700] : colors.white[900],
      surfaceAlt: isDark ? colors.dark[600] : colors.white[600],
      border: isDark
        ? alpha(colors.white[500], 0.09)
        : alpha(colors.dark[500], 0.1),
      textPrimary: colors.primary[100],
      textSecondary: isDark ? colors.gray[400] : colors.gray[300],
      gradient: `linear-gradient(135deg, ${accent} 0%, ${accent2} 100%)`,
      shadow: isDark
        ? "0 14px 34px -22px rgba(0,0,0,0.8)"
        : "0 14px 34px -24px rgba(16,24,74,0.35)",
    };
  }, [colors, isDark]);

  const orgColor = useCallback(
    (type) => (type === "others" ? colors.orange[500] : colors.newTeal[500]),
    [colors]
  );

  /* ---------------- Data ---------------- */
  const fetchData = useCallback(async () => {
    try {
      const [studentsResponse, parentsResponse, nextOfKeenResponse] =
        await Promise.all([
          getData("proccess.php", "students_V2"),
          getData("proccess.php", "parent_V2"),
          getData("proccess.php", "next_of_keen_V2"),
        ]);

      if (studentsResponse.success) setStudents(studentsResponse.data || []);
      else toast.error(studentsResponse.message || "Failed to load students");

      if (parentsResponse.success) setParents(parentsResponse.data || []);
      else toast.error(parentsResponse.message || "Failed to load parents");

      if (nextOfKeenResponse.success)
        setNextOfKeen(nextOfKeenResponse.data || []);
      else
        toast.error(nextOfKeenResponse.message || "Failed to load next of keen");
    } catch (err) {
      toast.error("Network error: Failed to fetch data");
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [getData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const parentName = useCallback(
    (id) => parents.find((p) => String(p.id) === String(id))?.fullName || "",
    [parents]
  );

  const nextOfKeenName = useCallback(
    (id) => nextOfKeen.find((n) => String(n.id) === String(id))?.fullName || "",
    [nextOfKeen]
  );

  /* ---------------- Filter options ---------------- */
  // Derived from the full student list, not the filtered one, so choosing a value
  // never empties the other dropdowns.
  const filterOptions = useMemo(() => {
    const organisations = new Set();
    const genders = new Set();
    const schools = new Set();
    const parentIds = new Set();
    const nextOfKeenIds = new Set();
    let anyUnlinkedParent = false;
    let anyUnlinkedNextOfKeen = false;

    students.forEach((s) => {
      const org = organisationText(s);
      if (org && org !== "—") organisations.add(org);
      if (s.gender) genders.add(String(s.gender).toLowerCase());
      if (s.school) schools.add(s.school);

      if (s.parent_id) parentIds.add(String(s.parent_id));
      else anyUnlinkedParent = true;

      if (s.next_of_keen_id) nextOfKeenIds.add(String(s.next_of_keen_id));
      else anyUnlinkedNextOfKeen = true;
    });

    const byLabel = (a, b) => a.label.localeCompare(b.label);

    return {
      organisations: [...organisations].sort((a, b) => a.localeCompare(b)),
      // Ordered by the GENDERS list, with any unexpected stored value kept visible.
      genders: [
        ...GENDERS.filter((g) => genders.has(g.value)).map((g) => g.value),
        ...[...genders].filter((g) => !GENDERS.some((x) => x.value === g)),
      ],
      schools: [...schools].sort((a, b) => a.localeCompare(b)),
      parents: [...parentIds]
        .map((id) => ({ id, label: parentName(id) || `Parent #${id}` }))
        .sort(byLabel),
      nextOfKeen: [...nextOfKeenIds]
        .map((id) => ({ id, label: nextOfKeenName(id) || `Next of keen #${id}` }))
        .sort(byLabel),
      anyUnlinkedParent,
      anyUnlinkedNextOfKeen,
    };
  }, [students, parentName, nextOfKeenName]);

  const activeFilterCount = [
    orgFilter,
    genderFilter,
    schoolFilter,
    parentFilter,
    nextOfKeenFilter,
  ].filter((v) => v !== ALL).length;

  const clearFilters = () => {
    setOrgFilter(ALL);
    setGenderFilter(ALL);
    setSchoolFilter(ALL);
    setParentFilter(ALL);
    setNextOfKeenFilter(ALL);
  };

  /* ---------------- Filtering & sorting ---------------- */
  const visibleStudents = useMemo(() => {
    const q = search.trim().toLowerCase();

    return students
      .filter((s) => {
        if (statusFilter === "all") return true;
        return statusFilter === "active"
          ? isActiveStudent(s)
          : !isActiveStudent(s);
      })
      .filter((s) => orgFilter === ALL || organisationText(s) === orgFilter)
      .filter(
        (s) =>
          genderFilter === ALL ||
          String(s.gender || "").toLowerCase() === genderFilter
      )
      .filter((s) => schoolFilter === ALL || s.school === schoolFilter)
      .filter((s) => {
        if (parentFilter === ALL) return true;
        if (parentFilter === UNLINKED) return !s.parent_id;
        return String(s.parent_id) === parentFilter;
      })
      .filter((s) => {
        if (nextOfKeenFilter === ALL) return true;
        if (nextOfKeenFilter === UNLINKED) return !s.next_of_keen_id;
        return String(s.next_of_keen_id) === nextOfKeenFilter;
      })
      .filter((s) => {
        if (!q) return true;
        return [
          s.firstName,
          s.lastName,
          s.email,
          s.cell,
          s.school,
          s.grade,
          s.gender,
          s.category,
          s.organisation,
          s.registration_type,
          parentName(s.parent_id),
          nextOfKeenName(s.next_of_keen_id),
        ]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q));
      })
      .sort((a, b) => {
        if (sortBy === "name") {
          return `${a.firstName} ${a.lastName}`.localeCompare(
            `${b.firstName} ${b.lastName}`
          );
        }
        const byDate = new Date(b.date || 0) - new Date(a.date || 0);
        if (byDate !== 0) return byDate;
        return Number(b.id) - Number(a.id);
      });
  }, [
    students,
    search,
    statusFilter,
    sortBy,
    orgFilter,
    genderFilter,
    schoolFilter,
    parentFilter,
    nextOfKeenFilter,
    parentName,
    nextOfKeenName,
  ]);

  const counts = useMemo(
    () => ({
      active: students.filter(isActiveStudent).length,
      inactive: students.filter((s) => !isActiveStudent(s)).length,
      all: students.length,
    }),
    [students]
  );

  /* ---------------- Export ---------------- */
  const exportToExcel = () => {
    if (visibleStudents.length === 0) {
      toast.warning("No data to export");
      return;
    }

    try {
      const rows = visibleStudents.map((student) => ({
        ID: student.id,
        "Organisation Type": student.organisation_type || "",
        Organisation: organisationText(student),
        "Registration Type": student.registration_type || "",
        Parent: parentName(student.parent_id) || "None",
        "Parent Relationship": student.parent_relationship || "",
        "Next of Keen": nextOfKeenName(student.next_of_keen_id) || "None",
        "First Name": student.firstName || "",
        "Last Name": student.lastName || "",
        Gender: genderLabel(student.gender),
        Category: student.category || "",
        Cell: student.cell || "",
        Email: student.email || "",
        Age: student.age || "",
        School: student.school || "",
        Grade: student.grade || "",
        "Physical Address": student.physicalAddress || "",
        "Student Token": student.studentToken || "",
        Status: isActiveStudent(student) ? "Active" : "Inactive",
        "Registration Date": student.date || "",
      }));

      const headers = Object.keys(rows[0]);
      const csv = [
        headers.join(","),
        ...rows.map((row) =>
          headers
            .map((h) => `"${String(row[h] ?? "").replace(/"/g, '""')}"`)
            .join(",")
        ),
      ].join("\n");

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `students_${new Date().toISOString().split("T")[0]}.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(`Exported ${visibleStudents.length} student(s)`);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export data");
    }
  };

  /* ---------------- Dialog handlers ---------------- */
  const handleOpenDialog = (isEdit = false, student = null) => {
    setIsEditing(isEdit);
    setSelectedStudentId(isEdit ? student.id : null);

    if (isEdit && student) {
      setFormData({
        ...EMPTY_FORM,
        organisation_type: student.organisation_type || "mia",
        organisation: student.organisation || "",
        registration_type: student.registration_type || "",
        next_of_keen_id: student.next_of_keen_id || "",
        parent_id: student.parent_id || "",
        parent_relationship: student.parent_relationship || "",
        firstName: student.firstName || "",
        lastName: student.lastName || "",
        gender: student.gender || "",
        category: student.category || "",
        cell: student.cell || "",
        email: student.email || "",
        password: "", // never pre-filled
        studentToken: student.studentToken || generateToken(),
        age: student.age || "",
        school: student.school || "",
        grade: student.grade || "",
        physicalAddress: student.physicalAddress || "",
        status: student.status !== undefined ? String(student.status) : "1",
        date: student.date || today(),
        image: student.image || "",
      });
      setParentMode(student.parent_id ? "existing" : "none");
      setNextOfKeenMode(student.next_of_keen_id ? "existing" : "none");
    } else {
      setFormData({ ...EMPTY_FORM, studentToken: generateToken() });
      setParentMode("none");
      setNextOfKeenMode("none");
    }

    setParentForm(EMPTY_PARENT);
    setNextOfKeenForm(EMPTY_NEXT_OF_KEEN);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedStudentId(null);
    setFormData(EMPTY_FORM);
    setParentForm(EMPTY_PARENT);
    setNextOfKeenForm(EMPTY_NEXT_OF_KEEN);
    setParentMode("none");
    setNextOfKeenMode("none");
  };

  // Clicking the backdrop must not discard a part-filled registration — the dialog
  // only closes via Cancel, Escape, or a successful save.
  const handleDialogClose = (_event, reason) => {
    if (reason === "backdropClick") return;
    handleCloseDialog();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      // "mia" students carry no organisation name of their own.
      if (name === "organisation_type" && value !== "others") {
        next.organisation = "";
      }
      return next;
    });
  };

  const handleParentFormChange = (e) => {
    const { name, value } = e.target;
    setParentForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleNextOfKeenFormChange = (e) => {
    const { name, value } = e.target;
    setNextOfKeenForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleParentModeChange = (e) => {
    const mode = e.target.value;
    setParentMode(mode);
    setParentForm(EMPTY_PARENT);
    if (mode !== "existing") {
      setFormData((prev) => ({ ...prev, parent_id: "" }));
    }
    if (mode === "none") {
      setFormData((prev) => ({ ...prev, parent_relationship: "" }));
    }
  };

  const handleNextOfKeenModeChange = (e) => {
    const mode = e.target.value;
    setNextOfKeenMode(mode);
    setNextOfKeenForm(EMPTY_NEXT_OF_KEEN);
    if (mode !== "existing") {
      setFormData((prev) => ({ ...prev, next_of_keen_id: "" }));
    }
  };

  /* ---------------- Validation & payload ---------------- */
  // The submit button is not a native form submit, so `required` alone would never
  // fire — everything mandatory is checked here.
  const validate = () => {
    const required = [
      ["registration_type", "Registration type"],
      ["firstName", "First name"],
      ["lastName", "Last name"],
      ["email", "Email"],
      ["cell", "Cell"],
      ["age", "Age"],
      ["school", "School"],
      ["grade", "Grade"],
      ["physicalAddress", "Physical address"],
      ["date", "Registration date"],
    ];

    for (const [key, label] of required) {
      if (!String(formData[key] ?? "").trim()) {
        toast.error(`${label} is required`);
        return false;
      }
    }

    if (!isEditing && !formData.password) {
      toast.error("Password is required");
      return false;
    }

    if (
      formData.organisation_type === "others" &&
      !formData.organisation.trim()
    ) {
      toast.error('Organisation is required when the type is "Others"');
      return false;
    }

    if (parentMode === "existing" && !formData.parent_id) {
      toast.error("Select a parent, or set the parent to Not linked");
      return false;
    }
    if (
      parentMode === "new" &&
      (!parentForm.fullName.trim() || !parentForm.email.trim())
    ) {
      toast.error("A new parent needs at least a full name and an email");
      return false;
    }

    if (nextOfKeenMode === "existing" && !formData.next_of_keen_id) {
      toast.error("Select a next of keen, or set it to Not linked");
      return false;
    }
    if (
      nextOfKeenMode === "new" &&
      (!nextOfKeenForm.fullName.trim() || !nextOfKeenForm.email.trim())
    ) {
      toast.error("A new next of keen needs at least a full name and an email");
      return false;
    }

    return true;
  };

  const buildStudentPayload = (parentId, nextOfKeenId) => {
    const source = {
      ...formData,
      parent_id: parentId || "",
      next_of_keen_id: nextOfKeenId || "",
    };
    if (!source.parent_id) source.parent_relationship = "";

    return STUDENT_COLUMNS.reduce((payload, key) => {
      const value = source[key];
      // Leave the stored password alone when the field is left blank on edit.
      if (key === "password" && isEditing && !value) return payload;
      if (NULLABLE_COLUMNS.includes(key) && String(value ?? "").trim() === "") {
        return payload;
      }
      payload[key] = typeof value === "string" ? value.trim() : value;
      return payload;
    }, {});
  };

  // Created before the student so the student row can reference the new id.
  const createParent = async () => {
    const payload = {
      fullName: parentForm.fullName.trim(),
      email: parentForm.email.trim(),
      password: parentForm.password,
      parentToken: generateToken(),
      cell: parentForm.cell.trim(),
      status: "1",
      date: today(),
    };

    const response = await addData("proccess.php", "parent_V2", payload);
    if (!response.success) {
      toast.error(response.message || "Failed to create the parent record");
      return null;
    }

    // The response carries every parent, so refresh the list from it and pick the
    // one matching the email just captured.
    if (Array.isArray(response.data)) setParents(response.data);

    const created = findRowByEmail(response.data, payload.email);
    if (!created?.id) {
      toast.error(
        `Parent saved, but could not be matched back by "${payload.email}". Link the parent manually.`
      );
      return null;
    }
    return created.id;
  };

  const createNextOfKeen = async () => {
    const payload = {
      fullName: nextOfKeenForm.fullName.trim(),
      email: nextOfKeenForm.email.trim(),
      cell: nextOfKeenForm.cell.trim(),
    };

    const response = await addData("proccess.php", "next_of_keen_V2", payload);
    if (!response.success) {
      toast.error(
        response.message || "Failed to create the next of keen record"
      );
      return null;
    }

    if (Array.isArray(response.data)) setNextOfKeen(response.data);

    const created = findRowByEmail(response.data, payload.email);
    if (!created?.id) {
      toast.error(
        `Next of keen saved, but could not be matched back by "${payload.email}". Link it manually.`
      );
      return null;
    }
    return created.id;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validate()) return;

    setSaving(true);
    try {
      // Order matters: the parent and next of keen rows must exist before the
      // student row can point at them.
      let parentId = parentMode === "existing" ? formData.parent_id : "";
      if (parentMode === "new") {
        parentId = await createParent();
        if (!parentId) return; // toast already raised, keep the dialog open
      }

      let nextOfKeenId =
        nextOfKeenMode === "existing" ? formData.next_of_keen_id : "";
      if (nextOfKeenMode === "new") {
        nextOfKeenId = await createNextOfKeen();
        if (!nextOfKeenId) return;
      }

      const payload = buildStudentPayload(parentId, nextOfKeenId);

      const response = isEditing
        ? await updateData(
            "proccess.php",
            "students_V2",
            payload,
            selectedStudentId
          )
        : await addData("proccess.php", "students_V2", payload);

      if (!response.success) {
        toast.error(response.message || "Operation failed");
        return; // keep the dialog open so the captured input is not lost
      }

      // The response carries every student, so prefer it over patching local state.
      if (Array.isArray(response.data)) {
        setStudents(response.data);
      } else if (isEditing) {
        setStudents((prev) =>
          prev.map((s) =>
            s.id === selectedStudentId ? { ...s, ...payload } : s
          )
        );
      }

      if (isEditing) {
        toast.success("Student updated");
      } else {
        const created = findRowByEmail(response.data, payload.email);
        if (!Array.isArray(response.data)) {
          // No list came back — fall back to appending what was submitted.
          setStudents((prev) => [...prev, { ...payload, id: created?.id }]);
        }
        toast.success(
          created?.id
            ? `Student added (#${created.id})`
            : "Student added"
        );
      }

      handleCloseDialog();
    } catch (err) {
      toast.error("Network error: Operation failed");
      console.error("Save error:", err);
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      const response = await deleteData(
        "proccess.php",
        "students_V2",
        studentToDelete.id
      );
      if (response.success) {
        toast.success("Student deleted");
        setStudents((prev) => prev.filter((s) => s.id !== studentToDelete.id));
        setSelectedStudent(null);
      } else {
        toast.error(response.message || "Failed to delete student");
      }
    } catch (err) {
      toast.error("Network error: Failed to delete student");
      console.error("Delete error:", err);
    }
    setStudentToDelete(null);
  };

  /* ---------------- Shared style helpers ---------------- */
  const initials = (s) =>
    `${s?.firstName?.[0] || ""}${s?.lastName?.[0] || ""}`.toUpperCase() || "?";

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      color: ui.textPrimary,
      bgcolor: ui.surfaceAlt,
      "& fieldset": { borderColor: ui.border },
      "&:hover fieldset": { borderColor: alpha(ui.accent, 0.5) },
      "&.Mui-focused fieldset": { borderColor: ui.accent },
    },
    "& .MuiInputLabel-root": { color: ui.textSecondary },
    "& .MuiInputLabel-root.Mui-focused": { color: ui.accent },
    "& .MuiFormHelperText-root": { color: ui.textSecondary },
  };

  const primaryBtn = {
    px: 2.75,
    py: 1.1,
    borderRadius: 2,
    fontWeight: 700,
    textTransform: "none",
    color: "#ffffff",
    background: ui.gradient,
    boxShadow: `0 10px 24px -12px ${alpha(ui.accent, 0.9)}`,
    "&:hover": { filter: "brightness(1.06)" },
  };

  const outlineBtn = (color) => ({
    px: 2.5,
    py: 1.05,
    borderRadius: 2,
    fontWeight: 700,
    textTransform: "none",
    color,
    border: `1px solid ${alpha(color, 0.45)}`,
    "&:hover": { bgcolor: alpha(color, 0.1), borderColor: color },
  });

  /* ------------------------------------------------------------------ */

  return (
    <Box sx={{ bgcolor: ui.pageBg, minHeight: "100%", pb: 4 }}>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <Container maxWidth="xl" sx={{ py: 3.5 }}>
        {/* ------------------------------------------------------------ */}
        {/* Header                                                        */}
        {/* ------------------------------------------------------------ */}
        <Stack
          direction={{ xs: "column", lg: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", lg: "center" }}
          justifyContent="space-between"
          sx={{ mb: 3 }}
        >
          <Box>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                letterSpacing: -0.4,
                color: ui.textPrimary,
              }}
            >
              Students
            </Typography>
            <Typography variant="h6" sx={{ mt: 0.5, color: ui.textSecondary }}>
              Click any student for full details and actions
            </Typography>
          </Box>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <TextField
              size="small"
              placeholder="Search name, email, school…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ ...fieldSx, minWidth: { sm: 260 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{ fontSize: 19, color: ui.textSecondary }}
                    />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              startIcon={<DownloadIcon />}
              onClick={exportToExcel}
              sx={outlineBtn(colors.green[500])}
            >
              Export
            </Button>
            <Tooltip
              title={
                canManageStudents
                  ? ""
                  : "Your role cannot register students"
              }
            >
              <span>
                <Button
                  startIcon={<AddCircleOutlineOutlinedIcon />}
                  onClick={() => handleOpenDialog(false)}
                  disabled={!canManageStudents}
                  sx={primaryBtn}
                >
                  Add student
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Stack>

        {/* ------------------------------------------------------------ */}
        {/* Filters                                                       */}
        {/* ------------------------------------------------------------ */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="space-between"
          sx={{ mb: 3 }}
        >
          <ToggleButtonGroup
            exclusive
            value={statusFilter}
            onChange={(_, v) => v !== null && setStatusFilter(v)}
            sx={{
              flexWrap: "wrap",
              gap: 1,
              "& .MuiToggleButton-root": {
                px: 2,
                py: 0.75,
                border: `1px solid ${ui.border} !important`,
                borderRadius: "999px !important",
                textTransform: "none",
                fontWeight: 700,
                fontSize: 13,
                color: ui.textSecondary,
                bgcolor: ui.surface,
                "&.Mui-selected": {
                  color: ui.accent,
                  bgcolor: alpha(ui.accent, 0.14),
                  borderColor: `${alpha(ui.accent, 0.5)} !important`,
                  "&:hover": { bgcolor: alpha(ui.accent, 0.2) },
                },
              },
            }}
          >
            {[
              { key: "active", label: "Active", count: counts.active },
              { key: "inactive", label: "Inactive", count: counts.inactive },
              { key: "all", label: "All", count: counts.all },
            ].map((f) => (
              <ToggleButton key={f.key} value={f.key}>
                {f.label}
                <Box
                  component="span"
                  sx={{
                    ml: 1,
                    px: 0.85,
                    borderRadius: 999,
                    fontSize: 11,
                    bgcolor: alpha(ui.textSecondary, 0.18),
                  }}
                >
                  {f.count}
                </Box>
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <ToggleButtonGroup
            exclusive
            size="small"
            value={sortBy}
            onChange={(_, v) => v !== null && setSortBy(v)}
            sx={{
              gap: 1,
              "& .MuiToggleButton-root": {
                px: 1.75,
                py: 0.6,
                border: `1px solid ${ui.border} !important`,
                borderRadius: "999px !important",
                textTransform: "none",
                fontWeight: 700,
                fontSize: 12.5,
                color: ui.textSecondary,
                bgcolor: ui.surface,
                "&.Mui-selected": {
                  color: ui.accent,
                  bgcolor: alpha(ui.accent, 0.14),
                  borderColor: `${alpha(ui.accent, 0.5)} !important`,
                },
              },
            }}
          >
            <ToggleButton value="newest">Newest first</ToggleButton>
            <ToggleButton value="name">A–Z</ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {/* ------------------------------------------------------------ */}
        {/* Dropdown filters                                              */}
        {/* ------------------------------------------------------------ */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 3,
            bgcolor: ui.surface,
            border: `1px solid ${ui.border}`,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 1.5 }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <FilterAltOutlinedIcon
                sx={{ fontSize: 18, color: ui.accent }}
              />
              <Typography
                variant="h6"
                sx={{
                  letterSpacing: 0.8,
                  textTransform: "uppercase",
                  fontWeight: 700,
                  color: ui.accent,
                }}
              >
                Filters
              </Typography>
              {activeFilterCount > 0 && (
                <Chip
                  size="small"
                  label={`${activeFilterCount} active`}
                  sx={{
                    height: 20,
                    fontWeight: 700,
                    fontSize: "0.68rem",
                    color: ui.accent,
                    bgcolor: alpha(ui.accent, 0.14),
                  }}
                />
              )}
            </Stack>

            {activeFilterCount > 0 && (
              <Button
                size="small"
                startIcon={<CloseIcon sx={{ fontSize: 15 }} />}
                onClick={clearFilters}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  color: ui.textSecondary,
                  "&:hover": { color: colors.red[500] },
                }}
              >
                Clear all
              </Button>
            )}
          </Stack>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Organisation"
                value={orgFilter}
                onChange={(e) => setOrgFilter(e.target.value)}
                sx={fieldSx}
              >
                <MenuItem value={ALL}>All organisations</MenuItem>
                {filterOptions.organisations.map((org) => (
                  <MenuItem key={org} value={org}>
                    {org}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Gender"
                value={genderFilter}
                onChange={(e) => setGenderFilter(e.target.value)}
                sx={fieldSx}
              >
                <MenuItem value={ALL}>All genders</MenuItem>
                {filterOptions.genders.map((g) => (
                  <MenuItem key={g} value={g}>
                    {genderLabel(g) || g}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="School"
                value={schoolFilter}
                onChange={(e) => setSchoolFilter(e.target.value)}
                sx={fieldSx}
              >
                <MenuItem value={ALL}>All schools</MenuItem>
                {filterOptions.schools.map((school) => (
                  <MenuItem key={school} value={school}>
                    {school}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Parent"
                value={parentFilter}
                onChange={(e) => setParentFilter(e.target.value)}
                sx={fieldSx}
              >
                <MenuItem value={ALL}>All parents</MenuItem>
                {filterOptions.anyUnlinkedParent && (
                  <MenuItem value={UNLINKED}>No parent linked</MenuItem>
                )}
                {filterOptions.parents.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Next of keen"
                value={nextOfKeenFilter}
                onChange={(e) => setNextOfKeenFilter(e.target.value)}
                sx={fieldSx}
              >
                <MenuItem value={ALL}>All next of keen</MenuItem>
                {filterOptions.anyUnlinkedNextOfKeen && (
                  <MenuItem value={UNLINKED}>No next of keen linked</MenuItem>
                )}
                {filterOptions.nextOfKeen.map((n) => (
                  <MenuItem key={n.id} value={n.id}>
                    {n.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          <Typography variant="h6" sx={{ mt: 1.5, color: ui.textSecondary }}>
            Showing {visibleStudents.length} of {students.length} student(s)
          </Typography>
        </Paper>

        {/* ------------------------------------------------------------ */}
        {/* Cards                                                         */}
        {/* ------------------------------------------------------------ */}
        {loading ? (
          <Grid container spacing={2.5}>
            {Array.from({ length: 8 }).map((_, i) => (
              <Grid size={{ xs: 12, sm: 6, lg: 3 }} key={`sk-${i}`}>
                <Skeleton
                  variant="rounded"
                  height={252}
                  sx={{
                    borderRadius: 3,
                    bgcolor: alpha(ui.textSecondary, 0.12),
                  }}
                />
              </Grid>
            ))}
          </Grid>
        ) : visibleStudents.length === 0 ? (
          <Stack alignItems="center" spacing={1.5} sx={{ py: 10 }}>
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, color: ui.textPrimary }}
            >
              No students found
            </Typography>
            <Typography variant="h6" sx={{ color: ui.textSecondary }}>
              {search
                ? "Nothing matches your search."
                : "Add a student to begin."}
            </Typography>
          </Stack>
        ) : (
          <Grid container spacing={2.5}>
            {visibleStudents.map((student) => {
              const oColor = orgColor(student.organisation_type);
              const active = isActiveStudent(student);

              return (
                <Grid size={{ xs: 12, sm: 6, lg: 3 }} key={student.id}>
                  <Paper
                    elevation={0}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedStudent(student)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setSelectedStudent(student);
                      }
                    }}
                    sx={{
                      height: "100%",
                      cursor: "pointer",
                      borderRadius: 3,
                      overflow: "hidden",
                      textAlign: "center",
                      bgcolor: ui.surface,
                      border: `1px solid ${ui.border}`,
                      boxShadow: ui.shadow,
                      opacity: active ? 1 : 0.72,
                      transition:
                        "transform .2s ease, border-color .2s ease, box-shadow .2s ease",
                      "&:hover, &:focus-visible": {
                        outline: "none",
                        transform: "translateY(-4px)",
                        borderColor: alpha(oColor, 0.5),
                        boxShadow: `0 16px 32px -20px ${alpha(oColor, 0.9)}`,
                      },
                    }}
                  >
                    {/* Banner */}
                    <Box
                      sx={{
                        height: 62,
                        background: `linear-gradient(135deg, ${alpha(
                          oColor,
                          isDark ? 0.3 : 0.22
                        )} 0%, ${alpha(
                          ui.accent2,
                          isDark ? 0.18 : 0.12
                        )} 100%)`,
                      }}
                    />

                    <Box sx={{ px: 2.5, pb: 2.5, mt: -4.5 }}>
                      <Box
                        sx={{ position: "relative", display: "inline-block" }}
                      >
                        <Avatar
                          src={imageUrl(student.image)}
                          sx={{
                            width: 76,
                            height: 76,
                            fontSize: 24,
                            fontWeight: 700,
                            bgcolor: ui.surfaceAlt,
                            color: ui.textPrimary,
                            border: `3px solid ${ui.surface}`,
                            boxShadow: ui.shadow,
                          }}
                        >
                          {initials(student)}
                        </Avatar>

                        {active && (
                          <Tooltip title="Active">
                            <VerifiedOutlinedIcon
                              sx={{
                                position: "absolute",
                                right: 2,
                                bottom: 2,
                                fontSize: 20,
                                color: colors.green[500],
                                bgcolor: ui.surface,
                                borderRadius: "50%",
                              }}
                            />
                          </Tooltip>
                        )}
                      </Box>

                      <Typography
                        variant="h4"
                        noWrap
                        sx={{
                          mt: 1.25,
                          fontWeight: 700,
                          color: ui.textPrimary,
                        }}
                      >
                        {student.firstName} {student.lastName}
                      </Typography>

                      <Stack
                        direction="row"
                        spacing={0.75}
                        justifyContent="center"
                        sx={{ mt: 1, mb: 1.75, flexWrap: "wrap", gap: 0.75 }}
                      >
                        <Chip
                          size="small"
                          icon={<BusinessOutlinedIcon sx={{ fontSize: 15 }} />}
                          label={organisationText(student)}
                          sx={{
                            height: 22,
                            maxWidth: "100%",
                            fontWeight: 700,
                            fontSize: "0.68rem",
                            color: oColor,
                            bgcolor: alpha(oColor, 0.14),
                            "& .MuiChip-icon": { color: "inherit" },
                          }}
                        />
                        {!active && (
                          <Chip
                            size="small"
                            label="Inactive"
                            sx={{
                              height: 22,
                              fontWeight: 700,
                              fontSize: "0.68rem",
                              color: colors.red[500],
                              bgcolor: alpha(colors.red[500], 0.14),
                            }}
                          />
                        )}
                      </Stack>

                      <Divider sx={{ borderColor: ui.border, mb: 1.5 }} />

                      <Stack spacing={0.85} sx={{ textAlign: "left" }}>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                        >
                          <MailOutlineIcon
                            sx={{
                              fontSize: 15,
                              color: ui.textSecondary,
                              flexShrink: 0,
                            }}
                          />
                          <Typography
                            variant="h6"
                            noWrap
                            sx={{ color: ui.textSecondary }}
                          >
                            {student.email || "—"}
                          </Typography>
                        </Stack>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                        >
                          <PhoneIphoneIcon
                            sx={{
                              fontSize: 15,
                              color: ui.textSecondary,
                              flexShrink: 0,
                            }}
                          />
                          <Typography
                            variant="h6"
                            noWrap
                            sx={{ color: ui.textSecondary }}
                          >
                            {student.cell || "—"}
                          </Typography>
                        </Stack>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                        >
                          <SchoolOutlinedIcon
                            sx={{
                              fontSize: 15,
                              color: ui.textSecondary,
                              flexShrink: 0,
                            }}
                          />
                          <Typography
                            variant="h6"
                            noWrap
                            sx={{ color: ui.textSecondary }}
                          >
                            {student.school || "No school"}
                            {student.grade ? ` · Grade ${student.grade}` : ""}
                          </Typography>
                        </Stack>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                        >
                          <FamilyRestroomOutlinedIcon
                            sx={{
                              fontSize: 15,
                              color: ui.textSecondary,
                              flexShrink: 0,
                            }}
                          />
                          <Typography
                            variant="h6"
                            noWrap
                            sx={{ color: ui.textSecondary }}
                          >
                            {parentName(student.parent_id) || "No parent"}
                          </Typography>
                        </Stack>
                      </Stack>
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Container>

      {/* -------------------------------------------------------------- */}
      {/* Detail dialog                                                   */}
      {/* -------------------------------------------------------------- */}
      <Dialog
        open={!!selectedStudent}
        onClose={() => setSelectedStudent(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: ui.surface,
            border: `1px solid ${ui.border}`,
            backgroundImage: "none",
          },
        }}
      >
        {selectedStudent && (
          <>
            <Box
              sx={{
                p: 3,
                display: "flex",
                alignItems: "center",
                gap: 2.25,
                background: `linear-gradient(180deg, ${alpha(
                  orgColor(selectedStudent.organisation_type),
                  isDark ? 0.18 : 0.11
                )} 0%, transparent 100%)`,
              }}
            >
              <Avatar
                src={imageUrl(selectedStudent.image)}
                sx={{
                  width: 78,
                  height: 78,
                  fontSize: 26,
                  fontWeight: 700,
                  bgcolor: ui.surfaceAlt,
                  color: ui.textPrimary,
                  border: `2px solid ${alpha(
                    orgColor(selectedStudent.organisation_type),
                    0.5
                  )}`,
                }}
              >
                {initials(selectedStudent)}
              </Avatar>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="h3"
                  sx={{ fontWeight: 800, color: ui.textPrimary }}
                >
                  {selectedStudent.firstName} {selectedStudent.lastName}
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mt: 0.85, flexWrap: "wrap", gap: 0.75 }}
                >
                  <Chip
                    size="small"
                    icon={<BusinessOutlinedIcon sx={{ fontSize: 15 }} />}
                    label={organisationText(selectedStudent)}
                    sx={{
                      fontWeight: 700,
                      color: orgColor(selectedStudent.organisation_type),
                      bgcolor: alpha(
                        orgColor(selectedStudent.organisation_type),
                        0.14
                      ),
                      "& .MuiChip-icon": { color: "inherit" },
                    }}
                  />
                  <Chip
                    size="small"
                    label={
                      isActiveStudent(selectedStudent) ? "Active" : "Inactive"
                    }
                    sx={{
                      fontWeight: 700,
                      color: isActiveStudent(selectedStudent)
                        ? colors.green[500]
                        : colors.red[500],
                      bgcolor: alpha(
                        isActiveStudent(selectedStudent)
                          ? colors.green[500]
                          : colors.red[500],
                        0.14
                      ),
                    }}
                  />
                  {selectedStudent.category && (
                    <Chip
                      size="small"
                      label={selectedStudent.category}
                      sx={{
                        fontWeight: 700,
                        color: ui.textSecondary,
                        bgcolor: alpha(ui.textSecondary, 0.14),
                      }}
                    />
                  )}
                </Stack>
              </Box>

              <IconButton
                onClick={() => setSelectedStudent(null)}
                sx={{
                  color: ui.textSecondary,
                  "&:hover": {
                    color: colors.red[500],
                    bgcolor: alpha(colors.red[500], 0.12),
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            <Divider sx={{ borderColor: ui.border }} />

            <DialogContent>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 1.5,
                      letterSpacing: 0.8,
                      textTransform: "uppercase",
                      fontWeight: 700,
                      color: ui.accent,
                    }}
                  >
                    Contact & schooling
                  </Typography>
                  <DetailRow ui={ui} label="Email" value={selectedStudent.email} />
                  <DetailRow ui={ui} label="Cell" value={selectedStudent.cell} />
                  <DetailRow ui={ui}
                    label="Gender"
                    value={genderLabel(selectedStudent.gender)}
                  />
                  <DetailRow ui={ui} label="Age" value={selectedStudent.age} />
                  <DetailRow ui={ui} label="School" value={selectedStudent.school} />
                  <DetailRow ui={ui} label="Grade" value={selectedStudent.grade} />
                  <DetailRow ui={ui}
                    label="Physical address"
                    value={selectedStudent.physicalAddress}
                  />
                  <DetailRow ui={ui}
                    label="Registered"
                    value={fmtDate(selectedStudent.date)}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 1.5,
                      letterSpacing: 0.8,
                      textTransform: "uppercase",
                      fontWeight: 700,
                      color: ui.accent,
                    }}
                  >
                    Registration & family
                  </Typography>
                  <DetailRow ui={ui}
                    label="Organisation type"
                    value={
                      selectedStudent.organisation_type === "others"
                        ? "Others"
                        : "Minds in Action"
                    }
                  />
                  <DetailRow ui={ui}
                    label="Organisation"
                    value={organisationText(selectedStudent)}
                  />
                  <DetailRow ui={ui}
                    label="Registration type"
                    value={selectedStudent.registration_type}
                  />
                  <DetailRow ui={ui}
                    label="Parent"
                    value={parentName(selectedStudent.parent_id) || "Not linked"}
                  />
                  <DetailRow ui={ui}
                    label="Parent relationship"
                    value={selectedStudent.parent_relationship}
                  />
                  <DetailRow ui={ui}
                    label="Next of keen"
                    value={
                      nextOfKeenName(selectedStudent.next_of_keen_id) ||
                      "Not linked"
                    }
                  />
                  <DetailRow ui={ui}
                    label="Student token"
                    value={selectedStudent.studentToken}
                  />
                </Grid>
              </Grid>
            </DialogContent>

            <Divider sx={{ borderColor: ui.border }} />

            <DialogActions sx={{ px: 3, py: 2.5, flexWrap: "wrap", gap: 1 }}>
              <Button
                startIcon={<DeleteForeverOutlinedIcon />}
                onClick={() => setStudentToDelete(selectedStudent)}
                disabled={!canManageStudents}
                sx={{ ...outlineBtn(colors.red[500]), mr: "auto" }}
              >
                Delete
              </Button>

              <Button
                startIcon={<EditNoteOutlinedIcon />}
                onClick={() => {
                  const student = selectedStudent;
                  setSelectedStudent(null);
                  handleOpenDialog(true, student);
                }}
                disabled={!canManageStudents}
                sx={primaryBtn}
              >
                Edit
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* -------------------------------------------------------------- */}
      {/* Add / Edit dialog                                               */}
      {/* -------------------------------------------------------------- */}
      <Dialog
        open={openDialog}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: ui.surface,
            border: `1px solid ${ui.border}`,
            backgroundImage: "none",
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography
            variant="h3"
            sx={{ fontWeight: 800, color: ui.textPrimary }}
          >
            {isEditing ? "Edit student" : "Register a student"}
          </Typography>
          {!isEditing && (
            <Typography variant="h6" sx={{ mt: 0.5, color: ui.textSecondary }}>
              The parent and next of keen are saved first, then the student is
              linked to them.
            </Typography>
          )}
        </DialogTitle>

        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ pt: 1.5 }}>
            {/* ---------- Step 1: Parent ---------- */}
            <FormSection ui={ui}
              title="1 · Parent"
              hint="Optional. Capture the parent first so the student can be linked to them."
            >
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  select
                  fullWidth
                  label="Parent"
                  value={parentMode}
                  onChange={handleParentModeChange}
                  sx={fieldSx}
                >
                  {LINK_MODES.map((m) => (
                    <MenuItem key={m.value} value={m.value}>
                      {m.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {parentMode === "existing" && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    select
                    fullWidth
                    label="Select parent"
                    name="parent_id"
                    value={formData.parent_id}
                    onChange={handleInputChange}
                    sx={fieldSx}
                  >
                    {parents.map((person) => (
                      <MenuItem key={person.id} value={person.id}>
                        {person.fullName} ({person.id})
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}

              {parentMode === "new" && (
                <>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Parent full name"
                      name="fullName"
                      value={parentForm.fullName}
                      onChange={handleParentFormChange}
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      type="email"
                      label="Parent email"
                      name="email"
                      value={parentForm.email}
                      onChange={handleParentFormChange}
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      type="tel"
                      label="Parent cell"
                      name="cell"
                      value={parentForm.cell}
                      onChange={handleParentFormChange}
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      type="password"
                      label="Parent password"
                      name="password"
                      value={parentForm.password}
                      onChange={handleParentFormChange}
                      helperText="Used for the parent portal login"
                      sx={fieldSx}
                    />
                  </Grid>
                </>
              )}

              {parentMode !== "none" && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Parent relationship"
                    name="parent_relationship"
                    value={formData.parent_relationship}
                    onChange={handleInputChange}
                    placeholder="Mother, Father, Guardian…"
                    sx={fieldSx}
                  />
                </Grid>
              )}
            </FormSection>

            {/* ---------- Step 2: Next of keen ---------- */}
            <FormSection ui={ui}
              title="2 · Next of keen"
              hint="Optional. Also saved before the student record is created."
            >
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  select
                  fullWidth
                  label="Next of keen"
                  value={nextOfKeenMode}
                  onChange={handleNextOfKeenModeChange}
                  sx={fieldSx}
                >
                  {LINK_MODES.map((m) => (
                    <MenuItem key={m.value} value={m.value}>
                      {m.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {nextOfKeenMode === "existing" && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    select
                    fullWidth
                    label="Select next of keen"
                    name="next_of_keen_id"
                    value={formData.next_of_keen_id}
                    onChange={handleInputChange}
                    sx={fieldSx}
                  >
                    {nextOfKeen.map((person) => (
                      <MenuItem key={person.id} value={person.id}>
                        {person.fullName} ({person.id})
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}

              {nextOfKeenMode === "new" && (
                <>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                      fullWidth
                      label="Full name"
                      name="fullName"
                      value={nextOfKeenForm.fullName}
                      onChange={handleNextOfKeenFormChange}
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                      fullWidth
                      type="email"
                      label="Email"
                      name="email"
                      value={nextOfKeenForm.email}
                      onChange={handleNextOfKeenFormChange}
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                      fullWidth
                      type="tel"
                      label="Cell"
                      name="cell"
                      value={nextOfKeenForm.cell}
                      onChange={handleNextOfKeenFormChange}
                      sx={fieldSx}
                    />
                  </Grid>
                </>
              )}
            </FormSection>

            {/* ---------- Step 3: Organisation ---------- */}
            <FormSection ui={ui} title="3 · Organisation">
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  select
                  fullWidth
                  label="Organisation type"
                  name="organisation_type"
                  value={formData.organisation_type}
                  onChange={handleInputChange}
                  required
                  sx={fieldSx}
                >
                  {ORGANISATION_TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {formData.organisation_type === "others" && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Organisation"
                    name="organisation"
                    value={formData.organisation}
                    onChange={handleInputChange}
                    required
                    helperText="Required when the organisation type is Others"
                    sx={fieldSx}
                  />
                </Grid>
              )}

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  select
                  fullWidth
                  label="Registration type"
                  name="registration_type"
                  value={formData.registration_type}
                  onChange={handleInputChange}
                  required
                  sx={fieldSx}
                >
                  {REGISTRATION_TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Category"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  sx={fieldSx}
                />
              </Grid>
            </FormSection>

            {/* ---------- Step 4: Student ---------- */}
            <FormSection ui={ui} title="4 · Student details">
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="First name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                  sx={fieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Last name"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                  sx={fieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  select
                  fullWidth
                  label="Gender"
                  name="gender"
                  value={formData.gender}
                  onChange={handleInputChange}
                  sx={fieldSx}
                >
                  <MenuItem value="">Not specified</MenuItem>
                  {GENDERS.map((g) => (
                    <MenuItem key={g.value} value={g.value}>
                      {g.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  type="email"
                  label="Email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  sx={fieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  type="tel"
                  label="Cell"
                  name="cell"
                  value={formData.cell}
                  onChange={handleInputChange}
                  placeholder="081XXXXXXX"
                  inputProps={{ maxLength: 15 }}
                  required
                  sx={fieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  type="password"
                  label="Password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required={!isEditing}
                  helperText={
                    isEditing ? "Leave blank to keep current password" : " "
                  }
                  sx={fieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  type="number"
                  label="Age"
                  name="age"
                  value={formData.age}
                  onChange={handleInputChange}
                  inputProps={{ min: 1, max: 120 }}
                  required
                  sx={fieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="School"
                  name="school"
                  value={formData.school}
                  onChange={handleInputChange}
                  required
                  sx={fieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Grade"
                  name="grade"
                  value={formData.grade}
                  onChange={handleInputChange}
                  required
                  sx={fieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Physical address"
                  name="physicalAddress"
                  value={formData.physicalAddress}
                  onChange={handleInputChange}
                  required
                  sx={fieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  select
                  fullWidth
                  label="Status"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  required
                  sx={fieldSx}
                >
                  <MenuItem value="1">Active</MenuItem>
                  <MenuItem value="0">Inactive</MenuItem>
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  type="date"
                  label="Registration date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                  required
                  sx={fieldSx}
                />
              </Grid>
            </FormSection>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={handleCloseDialog}
            disabled={saving}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              color: ui.textSecondary,
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving} sx={primaryBtn}>
            {saving ? "Saving…" : isEditing ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* -------------------------------------------------------------- */}
      {/* Delete dialog                                                   */}
      {/* -------------------------------------------------------------- */}
      <Dialog
        open={!!studentToDelete}
        onClose={() => setStudentToDelete(null)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: ui.surface,
            border: `1px solid ${ui.border}`,
            backgroundImage: "none",
          },
        }}
      >
        <DialogTitle>
          <Typography
            variant="h3"
            sx={{ fontWeight: 800, color: ui.textPrimary }}
          >
            Delete student
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography
            variant="h5"
            sx={{ color: ui.textSecondary, lineHeight: 1.6 }}
          >
            Permanently delete{" "}
            <Box
              component="span"
              sx={{ fontWeight: 800, color: ui.textPrimary }}
            >
              {studentToDelete?.firstName} {studentToDelete?.lastName}
            </Box>
            ? This cannot be undone. To keep their records, set their status to
            Inactive instead.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setStudentToDelete(null)}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              color: ui.textSecondary,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            startIcon={<DeleteForeverOutlinedIcon />}
            sx={{
              px: 2.75,
              py: 1.1,
              borderRadius: 2,
              fontWeight: 700,
              textTransform: "none",
              bgcolor: colors.red[500],
              "&:hover": { bgcolor: colors.red[600] },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Students;
